/**
 * components/riskTerminal/phone/PhoneTerminal.jsx
 *
 * upstox-076 — Positions on the phone, built to the Claude Design handoff "Positions · Phone · Option C"
 * (handover/design/positions-mobile-handoff-2026-10-10.html), with the trader's two changes
 * (HANDOVER.md 2026-10-10 "Phone layout adopted from Claude Design with two changes"):
 *   1. rows never move by themselves — at load groups with a problem are open and fine groups closed;
 *      after load a group opens or closes only when the trader taps it;
 *   2. the trend cells are tappable — the same switch call, recorded the same way, as the desktop.
 *
 * Layout only. The same panel, the same callbacks, the same requests as RiskTerminal (the desktop, which
 * this file does not touch): every figure comes from model.js. Three tabs (Positions · Orders · Watchlist)
 * behind a bottom bar; a tap on a leg opens a bottom sheet. Actions that need the desktop say so
 * ("Answer on the desktop", TAG is inert, no B / S, no exit buttons on a read-only account).
 *
 * Nothing here is wider than the viewport: every row is `minmax(0, 1fr)` plus fixed numeric columns that
 * add up to less than 300 px, so 360–430 px never scrolls sideways.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { C, FONT, money, badge, plainNotGraded, inr0, hhmm, mmss, loadIcons, HATCH } from '../theme';
import {
  gradeKind, notGradedTag, gradeTitle, trendOf, feedFaults, moneyOf, conditionView, dashReason, sideLine, fillLine, trendStrip, freshnessOf,
  qtyText, priceText, avgText, rowCharges, chargesToday, sumKnown, UPSTOX_SIGNIN_URL, orderTerms, closeness, mcxNote, agoText,
} from '../model';
import { accountName, accountColor } from '../accounts';
import { clientIdOf } from '../StatusStrip';
import { realApi } from '../api';
import { openUpstoxSignin } from '../signin';
import { noYear, exceptionOf, edgeOf } from '../RiskTerminal';
import { headerLine, statusLine } from '../Watchlist';
import { Refusal } from '../controls';
import {
  expiryText, sharedExpiry, legName, stopMark, instrumentTitle, sheetFields, stopBanner, heldIndex, heldChip, limitBar, shortRupees,
  shortWords, doneStatus, dayMon,
} from './phoneModel';

const OK_GREEN = '#2fa35a';
const NUM = { fontVariantNumeric: 'tabular-nums' };
const RESET = { appearance: 'none', background: 'transparent', border: 'none', padding: 0, margin: 0, font: 'inherit', color: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%', display: 'block' };
const CAP = { fontSize: '9.5px', letterSpacing: '0.12em', color: C.ink4 };
const WATCH_PREF = 'riskTerminal.watch.v1';
const TAB_H = 56;                                  // the bottom bar's touch height; the safe-area inset is added under it

const SCOPED_CSS = `
.rt-phone, .rt-phone * { box-sizing:border-box; }
.rt-phone { -webkit-text-size-adjust:100%; -webkit-tap-highlight-color: transparent; }
.rt-phone button { font-family: inherit; }
.rt-phone .rt-tip { text-decoration: underline dotted #878176; text-underline-offset: 3px; cursor: default; }
.rt-phone button:focus-visible, .rt-phone a:focus-visible { outline: 2px solid #1a1917; outline-offset: -2px; }
`;

// ── small parts ───────────────────────────────────────────────────────────

/** A value that explains itself: the desktop's hover card on a pointer, the same card on a tap. */
function PTip({ text, head, tone, dotted, style, children, as: Tag = 'span', ...rest }) {
  return (
    <Tag className={dotted ? 'rt-tip' : undefined} data-tip={text || undefined} data-tip-head={head || undefined} data-tip-tone={tone || undefined}
      onClick={text ? e => e.stopPropagation() : undefined} style={{ ...(text ? { cursor: 'pointer' } : {}), ...style }} {...rest}>{children}</Tag>
  );
}

const SVG = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'square', 'aria-hidden': true };
const IconPositions = () => <svg {...SVG}><path d="M12 3 3 8l9 5 9-5-9-5Z" /><path d="m3 13 9 5 9-5" /><path d="m3 17.5 9 5 9-5" opacity=".55" /></svg>;
const IconOrders = () => <svg {...SVG}><path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" /><path d="M9 8h6M9 12h6" /></svg>;
const IconList = () => <svg {...SVG}><path d="M4 6h16M4 12h16M4 18h16" /></svg>;

function Chip({ red, children, style, ...rest }) {
  return <span style={{ fontSize: '9px', letterSpacing: '0.1em', fontWeight: 700, padding: '2px 5px', color: red ? '#fff' : C.amber, background: red ? C.red : 'transparent', border: red ? 'none' : '1px solid ' + C.amber, whiteSpace: 'nowrap', ...style }} {...rest}>{children}</span>;
}

/** the back gesture closes a sheet: a history entry while it is open */
function useSheetBack(open, onClose) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    let popped = false;
    try { window.history.pushState({ rtSheet: true }, ''); } catch (e) { /* no history: tap and swipe still close it */ }
    const onPop = () => { popped = true; close.current(); };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (!popped) { try { if (window.history.state && window.history.state.rtSheet) window.history.back(); } catch (e) { /* ignore */ } }
    };
  }, [open]);
}

// ── the tap card: what the desktop shows on hover (`data-tip` text), shown on a tap ──

const TIP_TONE = { red: C.red, amber: C.amber };

/**
 * One card for the whole phone screen. Any element inside it that carries `data-tip` opens the card on a tap
 * (the same text, head and tone the desktop's hover card reads); a tap anywhere else, or on the same value
 * again, closes it. Placed inside the screen, kept 4 px inside both edges, flipped above near the bottom bar.
 */
function TapCard({ rootRef }) {
  const [card, setCard] = useState(null);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = e => {
      const t = e.target;
      if (t && t.closest && t.closest('[data-tap-card]')) return;
      const el = t && t.closest ? t.closest('[data-tip]') : null;
      const root = rootRef.current;
      if (!el || !root || !root.contains(el) || !el.getAttribute('data-tip')) { setCard(null); return; }
      const r = el.getBoundingClientRect(), b = root.getBoundingClientRect();
      setCard(c => (c && c.el === el ? null : { el, text: el.getAttribute('data-tip'), head: el.getAttribute('data-tip-head'), tone: el.getAttribute('data-tip-tone'),
        left: r.left - b.left, top: r.bottom - b.top + 6, above: r.top - b.top - 6, bottom: r.bottom, width: b.width }));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [rootRef]);
  useLayoutEffect(() => {
    if (!card || !ref.current) { setPos(null); return; }
    const w = Math.min(280, card.width - 8), h = ref.current.offsetHeight;
    const flip = card.bottom + h > window.innerHeight - TAB_H - 8 && card.above - h > 4;
    setPos({ left: Math.max(4, Math.min(card.left, card.width - w - 4)), top: flip ? card.above - h : card.top, width: w });
  }, [card]);
  if (!card) return null;
  const lines = String(card.text).split('\n');
  return (
    <div ref={ref} data-tap-card role="tooltip" style={{ position: 'absolute', left: pos ? pos.left : 0, top: pos ? pos.top : 0, width: Math.min(280, card.width - 8), zIndex: 95, padding: '9px 12px', background: '#fff', border: '1px solid #cfc9bd',
      boxShadow: '0 8px 20px -8px rgba(26,25,23,.28)', visibility: pos ? 'visible' : 'hidden' }}>
      {card.head && <div style={{ fontSize: '9.5px', letterSpacing: '0.14em', fontWeight: 700, color: TIP_TONE[card.tone] || C.ink4, marginBottom: '3px' }}>{card.head}</div>}
      {lines.map((ln, i) => <div key={i} style={{ fontSize: '12px', lineHeight: 1.5, color: C.ink }}>{ln}</div>)}
    </div>
  );
}

// ── the app bar: account switch · status dot ──────────────────────────────

function AppBar({ accounts, account, status, onSwitch, fresh, dataAgeMs, tone }) {
  const [card, setCard] = useState(false);
  const dot = tone === 'red' ? C.red : tone === 'amber' ? '#c98a12' : tone === 'grey' ? C.line4 : OK_GREEN;
  const list = accounts || [];
  return (
    <div data-app-bar style={{ minHeight: '52px', display: 'flex', alignItems: 'center', gap: '4px', padding: '0 4px 0 14px', position: 'relative' }}>
      {list.length > 0 && account ? (
        <div data-account-switch role="group" aria-label="Account" style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: list.map(a => (a.broker === 'KITE' ? '1fr' : '1fr')).join(' '), gap: '2px', padding: '2px', border: '1px solid #cfc9bd', background: '#fff' }}>
          {list.map(a => {
            const col = accountColor(list, a);
            const on = a.id === account.id && a.broker === account.broker;
            const id = on ? clientIdOf(a, status) : '';
            return (
              <button key={a.id == null ? 'own' : a.id} type="button" data-account={accountName(a)} data-active={on ? 'true' : undefined} aria-pressed={on}
                onClick={() => !on && onSwitch && onSwitch(a)}
                style={{ minWidth: 0, height: '38px', padding: '0 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px', border: 'none', borderRadius: 0, cursor: on ? 'default' : 'pointer', background: on ? col.ink : 'transparent', lineHeight: 1.15 }}>
                <span style={{ fontSize: '10px', letterSpacing: '0.08em', fontWeight: on ? 700 : 600, color: on ? '#fff' : C.ink3, whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{accountName(a)}</span>
                {on && id && <span data-client-id style={{ fontSize: '10.5px', color: col.id, ...NUM, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{id}</span>}
              </button>
            );
          })}
        </div>
      ) : <div style={{ flex: 1 }} />}
      <button type="button" data-status-dot={tone} aria-label="Feed status" aria-expanded={card} onClick={() => setCard(o => !o)}
        style={{ ...RESET, width: '44px', height: '44px', flex: '0 0 44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot, display: 'block' }} />
      </button>
      {card && (
        <>
          <div onClick={() => setCard(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div data-status-card role="dialog" aria-label="Feed status" style={{ position: 'absolute', right: '8px', top: '48px', zIndex: 41, width: 'min(300px, calc(100% - 16px))', padding: '10px 12px', background: '#fff', border: '1px solid #cfc9bd', boxShadow: '0 8px 20px -8px rgba(26,25,23,.28)' }}>
            <div style={{ ...CAP, fontWeight: 700, marginBottom: '6px' }}>FEEDS</div>
            {fresh.items.length === 0 && <div style={{ fontSize: '12px', color: C.ink, lineHeight: 1.5 }}>{fresh.grey}</div>}
            {fresh.items.map((i, k) => (
              <div key={k} data-fault={i.name} style={{ fontSize: '12px', lineHeight: 1.5, color: C.ink, marginBottom: '4px' }}>
                <span style={{ fontSize: '10px', letterSpacing: '0.1em', fontWeight: 700, color: i.tone === 'red' ? C.red : C.amber, marginRight: '6px' }}>{i.name}</span>
                {i.text}
                {i.hover && <div style={{ fontSize: '11px', color: C.ink4 }}>{i.hover}</div>}
                {i.signin && <a href={UPSTOX_SIGNIN_URL} target="_blank" rel="noopener noreferrer" onClick={openUpstoxSignin} style={{ display: 'inline-block', marginTop: '2px', color: C.red, fontWeight: 700, fontSize: '11px', letterSpacing: '0.1em', minHeight: '32px', lineHeight: '32px' }}>SIGN IN</a>}
              </div>
            ))}
            <div data-data-age style={{ fontSize: '11px', color: C.ink4, marginTop: '4px', ...NUM }}>{dataAgeMs != null ? 'DATA ' + agoText(dataAgeMs / 1000) + ' OLD' : 'NO DATA YET'}</div>
          </div>
        </>
      )}
    </div>
  );
}

// ── trend: two tappable cells ─────────────────────────────────────────────

function TrendCells({ strip, trend, busy, onPick }) {
  return (
    <div data-trend role="radiogroup" aria-label="Trend method" style={{ margin: '4px 14px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid ' + C.line2 }}>
      {strip.rows.map((r, i) => {
        const mark = r.selected && strip.known ? (strip.solid ? '2.5px solid ' + C.ink : '2.5px dashed ' + C.ink) : null;
        return (
          <button key={r.method} type="button" role="radio" aria-checked={r.selected} data-trend-cell={r.method} data-selected={r.selected ? 'true' : undefined}
            disabled={busy && !r.selected} onClick={() => !r.selected && !busy && onPick(r.method)}
            style={{ ...RESET, minHeight: '46px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px', minWidth: 0, borderLeft: i ? '1px solid ' + C.line2 : 'none', background: r.selected ? C.paper2 : '#fff', cursor: r.selected ? 'default' : 'pointer' }}>
            <span aria-hidden="true" style={{ width: '12px', height: '12px', flex: '0 0 12px', borderRadius: '50%', border: '1.5px solid ' + (r.selected ? C.ink : C.ink5), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {r.selected && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: C.ink }} />}
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: 1.2, minWidth: 0 }}>
              <span style={{ fontSize: '12.5px', fontWeight: r.selected ? 500 : 400, color: r.selected ? C.ink : C.ink5 }}>{r.name}</span>
              <span data-reading={r.method} data-tip={r.selected ? trend.sub : undefined} style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                {mark && <span style={{ width: '12px', height: 0, flex: '0 0 12px', borderTop: mark }} />}
                <span style={{ fontSize: '10.5px', letterSpacing: '0.07em', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: !r.ready ? C.ink5 : r.differs ? C.amber : r.selected ? C.ink : C.ink4 }}>{shortWords(r.words)}</span>
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── issues ────────────────────────────────────────────────────────────────

function IssueBox({ v, lead, nested }) {
  const interrupt = v.kindCode === 'INTERRUPT_DUE';
  return (
    <div data-issue={v.kindCode} data-issue-tone={v.red ? 'red' : 'amber'} style={{ padding: '10px 12px', background: v.red ? C.redTint : C.amberTint, border: '1px solid ' + (v.red ? C.redLine : C.amberLine), display: 'flex', flexDirection: 'column', gap: '5px', marginTop: nested ? '6px' : 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingRight: lead ? lead : 0 }}>
        <Chip red={v.red}>{v.kind}</Chip>
        {v.timer && <span style={{ fontSize: '12.5px', fontWeight: 600, color: C.amber, ...NUM }}>{v.timer}</span>}
      </div>
      <PTip text={v.keyTip || ''} style={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.35, display: 'block' }}>{v.text}</PTip>
      {interrupt && <div data-answer-note style={{ fontSize: '12.5px', fontWeight: 700, color: C.red }}>Answer on the desktop</div>}
    </div>
  );
}

function Issues({ views, open, onToggle }) {
  if (!views.length) return null;
  const [first, ...rest] = views;
  return (
    <div data-issues style={{ margin: '8px 14px 0', position: 'relative' }}>
      <IssueBox v={first} lead={rest.length ? '96px' : 0} />
      {rest.length > 0 && (
        <button type="button" data-issues-more aria-expanded={open} onClick={onToggle}
          style={{ ...RESET, width: 'auto', position: 'absolute', top: '4px', right: '4px', minHeight: '32px', padding: '0 6px', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: 700, color: rest[0].red ? C.red : C.amber, letterSpacing: '0.04em', textTransform: 'uppercase', maxWidth: '60%' }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>+{rest.length} {rest[0].kind}</span><span aria-hidden="true">{open ? '▴' : '▾'}</span>
        </button>
      )}
      {open && rest.map(v => <IssueBox key={v.id} v={v} nested />)}
    </div>
  );
}

// ── the day band ──────────────────────────────────────────────────────────

function DayBand({ M, P, CT }) {
  const total = money(M.computed ? M.total : null, 38);
  const len = total.text.length;
  const size = len <= 8 ? 38 : len <= 10 ? 32 : 26;
  const real = money(M.computed ? M.realised : null, 13), unreal = money(M.computed ? M.unrealised : null, 13);
  const bar = limitBar(M, P.money);
  const col = bar ? (bar.tone === 'red' ? C.red : bar.tone === 'amber' ? C.amber : C.line4) : C.line4;
  return (
    <div data-day-band data-past-limit={M.over ? 'true' : undefined} style={{ marginTop: '10px', padding: '10px 14px 12px', borderTop: '1px solid ' + C.line, borderBottom: '1px solid ' + C.line, display: 'flex', flexDirection: 'column', gap: '8px', background: M.over ? C.redTint : '#fff' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '12px', alignItems: 'end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <span style={{ fontSize: '10px', letterSpacing: '0.12em', fontWeight: 700, color: C.ink4 }}>{M.computed ? (M.total < 0 ? 'TODAY · DOWN' : M.total > 0 ? 'TODAY · UP' : 'TODAY') : 'TODAY'}</span>
          <span data-day-total data-tip={M.computed ? undefined : M.reason || ''} style={{ ...total.style, fontSize: size + 'px', lineHeight: 1, whiteSpace: 'nowrap', fontWeight: M.computed && M.total < 0 ? 600 : 400 }}>{total.text}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: '10px', rowGap: '1px', fontSize: '13px', lineHeight: 1.35, paddingBottom: '2px' }}>
          <span style={{ ...CAP, fontSize: '9.5px', alignSelf: 'center' }}>REALISED</span>
          <span style={{ textAlign: 'right', fontWeight: M.realised < 0 ? 600 : 400, color: real.unknown ? C.ink5 : C.ink, ...NUM }}>{real.text}</span>
          <span style={{ ...CAP, fontSize: '9.5px', alignSelf: 'center' }}>UNREALISED</span>
          <span style={{ textAlign: 'right', fontWeight: M.unrealised < 0 ? 600 : 400, color: unreal.unknown ? C.ink5 : C.ink, ...NUM }}>{unreal.text}</span>
          <span style={{ ...CAP, fontSize: '9.5px', alignSelf: 'center' }}>CHARGES</span>
          <PTip dotted text={CT.title} head="CHARGES TODAY" style={{ textAlign: 'right', color: C.ink3, ...NUM }}>{CT.text}</PTip>
        </div>
      </div>
      {M.computed && M.noLimit && <div data-limit-label style={{ fontSize: '10.5px', letterSpacing: '0.1em', fontWeight: 600, color: C.ink3 }}>no day limit set</div>}
      {!M.computed && <div style={{ fontSize: '10.5px', letterSpacing: '0.08em', color: C.ink5 }}>UNKNOWN · {String(M.reason || 'not computed').toUpperCase()}</div>}
      {bar && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ ...CAP }}>DAY LIMIT {shortRupees(M.limit)}</span>
            <span data-limit-label style={{ fontSize: '10.5px', letterSpacing: '0.08em', fontWeight: 600, color: bar.tone === 'grey' ? C.ink3 : col, whiteSpace: 'nowrap', ...NUM }}>{bar.label}</span>
          </div>
          <div style={{ position: 'relative', height: '6px', background: C.paper3, border: '1px solid ' + C.line }}>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: bar.fillPct + '%', background: col }} />
            {bar.amberPct != null && <div style={{ position: 'absolute', top: '-3px', bottom: '-3px', left: Math.min(bar.amberPct, 100) + '%', width: '2px', background: C.amber }} />}
            {bar.redPct != null && <div style={{ position: 'absolute', top: '-3px', bottom: '-3px', left: Math.min(bar.redPct, 100) + '%', width: '2px', background: C.red }} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ── positions ─────────────────────────────────────────────────────────────

const COLS = 'minmax(0,1fr) 44px 52px 52px 70px';

function ColumnHeader() {
  return (
    <div data-col-head style={{ display: 'grid', gridTemplateColumns: COLS, gap: '6px', padding: '12px 14px 4px', alignItems: 'baseline', ...CAP }}>
      <span style={{ fontSize: '11px', letterSpacing: '0.14em', fontWeight: 600, color: C.ink }}>POSITIONS</span>
      <span style={{ textAlign: 'right' }}>QTY</span><span style={{ textAlign: 'right' }}>AVG</span><span style={{ textAlign: 'right' }}>LTP</span><span style={{ textAlign: 'right' }}>P&amp;L</span>
    </div>
  );
}

function StopMark({ mark }) {
  if (mark === 'failed') return <span data-stop-mark="failed" aria-label="stop failed" data-tip="stop failed to place" style={{ width: '7px', height: '7px', background: C.red, display: 'inline-block', flex: '0 0 7px' }} />;
  if (mark === 'hedge') return <span data-stop-mark="hedge" aria-label="hedge leg, no stop needed" style={{ width: '8px', height: '1px', background: C.ink5, display: 'inline-block', flex: '0 0 8px' }} />;
  return null;
}

function LegRow({ l, symbolOf, withExpiry, onOpen }) {
  const pl = money(l.pnl, 13);
  const unknown = l.pnl == null ? (l.pnl_reason || 'no broker row for it') : '';
  const mcx = mcxNote(l.instrument_key);
  return (
    <button type="button" data-leg={l.leg_id ?? l.instrument_key} onClick={onOpen}
      style={{ ...RESET, display: 'grid', gridTemplateColumns: COLS, gap: '6px', alignItems: 'center', minHeight: '30px', fontSize: '13px', color: C.ink2, ...NUM }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: C.ink, minWidth: 0 }}>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.2, padding: withExpiry ? '3px 0' : 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{legName(l, symbolOf, false)}</span>
          {withExpiry && l.expiry && <span data-leg-expiry style={{ fontSize: '10.5px', color: C.ink4 }}>{dayMon(l.expiry)}</span>}
        </span>
        <StopMark mark={stopMark(l)} />
        {mcx && <span style={{ fontSize: '8.5px', letterSpacing: '0.08em', color: C.ink4, whiteSpace: 'nowrap' }}>MCX</span>}
      </span>
      <span style={{ textAlign: 'right' }}>{qtyText(l.quantity)}</span>
      <span style={{ textAlign: 'right', color: C.ink4 }}>{avgText(l.average_price)}</span>
      <span style={{ textAlign: 'right' }}>{priceText(l.last_price)}</span>
      <PTip text={unknown} head="P&L" style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: pl.text.length > 10 ? '11px' : pl.text.length > 9 ? '12px' : '13px', fontWeight: l.pnl < 0 ? 600 : 400, color: pl.unknown ? C.ink5 : l.pnl < 0 ? C.ink : C.ink3 }}>{pl.text}</PTip>
    </button>
  );
}

/** One group (or holding): the header row, its tags and meta line, and — when open — a row per leg. */
function GroupBlock({ g, open, onToggle, onLeg, symbolOf }) {
  const legsMixed = new Set(g.legs.map(l => l.expiry).filter(Boolean)).size > 1;
  return (
    <div data-group={g.id} data-open={open ? 'true' : 'false'} data-grade={g.kind} style={{ padding: '2px 14px 6px', background: g.tint, borderTop: '1px solid ' + C.paper3 }}>
      <button type="button" data-group-toggle aria-expanded={open} onClick={onToggle} style={{ ...RESET }}>
        <span style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '6px', alignItems: 'center', minHeight: '38px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
            <span style={{ fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
            <span aria-hidden="true" style={{ fontSize: '12px', color: C.ink5 }}>{open ? '▴' : '▾'}</span>
          </span>
          <PTip text={g.plTip} head="P&L" style={{ textAlign: 'right', fontSize: '16px', fontWeight: g.pnl != null && g.pnl < 0 ? 600 : 400, color: g.pnl == null ? C.ink5 : g.pnl < 0 ? C.ink : C.ink3, ...NUM }}>{g.plText}</PTip>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px', marginTop: '-4px', minHeight: '20px' }}>
          {g.tags.map((t, i) => (t.kind === 'against'
            ? <React.Fragment key={i}><Chip red={t.red} data-tag="against" data-tip={t.tip} style={t.red ? undefined : { padding: '2px 5px' }}>AGAINST TREND</Chip>{t.timer && <span data-timer style={{ fontSize: '11.5px', fontWeight: 600, color: t.due ? C.red : C.amber, ...NUM }}>{t.timer}</span>}</React.Fragment>
            : t.kind === 'nostop' ? <Chip key={i} red data-tag="nostop" style={{ letterSpacing: '0.08em' }}>NO STOP · {t.leg}</Chip>
            : <PTip key={i} text={t.tip} data-tag="notgraded" style={t.style}>{t.label}</PTip>))}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '11px', color: C.ink4, display: 'flex', gap: '4px', alignItems: 'baseline', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {!open && <span>{g.nLegs}</span>}
            {g.expiry && <span>{(!open ? '· ' : '') + g.expiry}</span>}
            <span>{(g.expiry || !open ? '· ' : '') + 'chg '}<PTip dotted text={g.ch.title} head={g.ch.head || 'CHARGES'}>{g.ch.text}</PTip></span>
          </span>
        </span>
      </button>
      {g.note && <div style={{ fontSize: '11px', color: C.ink4, padding: '2px 0' }}>{g.note}</div>}
      {open && <div data-legs style={{ display: 'flex', flexDirection: 'column', paddingTop: '4px' }}>{g.legs.map(l => <LegRow key={l.leg_id ?? l.instrument_key} l={l} symbolOf={symbolOf} withExpiry={legsMixed} onOpen={() => onLeg(g, l)} />)}</div>}
    </div>
  );
}

// ── the detail sheet ──────────────────────────────────────────────────────

function Sheet({ g, l, index, symbolOf, positionsReason, account, onClose }) {
  const startY = useRef(null);
  const [drag, setDrag] = useState(0);
  const body = useRef(null);
  const fields = sheetFields(l, symbolOf, positionsReason);
  const pl = money(l.pnl, 16);
  const banner = stopBanner(l);
  const kite = account && account.broker === 'KITE';
  const onStart = e => { startY.current = e.touches[0].clientY; };
  const onMove = e => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0 && (!body.current || body.current.scrollTop <= 0)) setDrag(dy);
  };
  const onEnd = () => { const d = drag; startY.current = null; setDrag(0); if (d > 80) onClose(); };
  useEffect(() => {
    const k = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div data-sheet-root style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
      <div data-sheet-scrim onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(26,25,23,.38)' }} />
      <div ref={body} data-sheet role="dialog" aria-label={instrumentTitle(l, symbolOf)} onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
        style={{ position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight: '88%', overflowY: 'auto', overflowX: 'hidden', background: '#fff', borderTop: '1px solid #cfc9bd', padding: '10px 18px calc(20px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: '14px', boxShadow: '0 -10px 30px rgba(26,25,23,.18)', transform: drag ? 'translateY(' + drag + 'px)' : 'none', transition: drag ? 'none' : 'transform .15s' }}>
        <button type="button" aria-label="Close" onClick={onClose} style={{ ...RESET, width: '100%', height: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '-4px' }}>
          <span style={{ width: '36px', height: '4px', borderRadius: '2px', background: C.line }} />
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <span data-sheet-head style={{ ...CAP, textTransform: 'uppercase' }}>{g.name} · LEG {index.n} OF {index.of}</span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{ fontSize: '18px', fontWeight: 600, minWidth: 0 }}>{instrumentTitle(l, symbolOf)}</span>
            <span style={{ flex: 1 }} />
            <PTip text={l.pnl == null ? (l.pnl_reason || positionsReason || 'no broker row for it') : ''} head="P&L" style={{ fontSize: '16px', color: pl.unknown ? C.ink5 : l.pnl < 0 ? C.ink : C.ink3, fontWeight: l.pnl < 0 ? 600 : 400, ...NUM }}>{pl.text}</PTip>
          </div>
        </div>
        {banner && (
          <div data-sheet-banner style={{ padding: '10px 12px', background: C.redTint, border: '1px solid ' + C.redLine, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Chip red>NO STOP</Chip><span style={{ fontSize: '12.5px', color: C.ink2 }}>{banner}</span>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', rowGap: '12px', columnGap: '10px' }}>
          {fields.map(f => (
            <div key={f.k} data-field={f.k} style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
              <span style={CAP}>{f.k}</span>
              <PTip text={f.tip} dotted={!!f.tip && (f.dotted || f.v === '—')} head={f.k === 'CHARGES' ? 'CHARGES' : undefined} style={{ fontSize: '14px', ...NUM, overflowWrap: 'anywhere', color: f.v.startsWith('■') ? C.red : C.ink }}>{f.v}</PTip>
            </div>
          ))}
        </div>
        {kite ? (
          <div data-readonly style={{ fontSize: '12px', color: C.ink4, letterSpacing: '0.04em' }}>Read-only account</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {['EXIT LEG', 'EXIT GROUP'].map(t => (
                <button key={t} type="button" disabled data-exit={t} aria-label={t + ' (not available yet)'}
                  style={{ height: '46px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed ' + C.line4, background: 'transparent', color: C.ink4, fontSize: '11px', letterSpacing: '0.1em', fontWeight: 600, cursor: 'not-allowed' }}>{t}</button>
              ))}
            </div>
            <span style={{ fontSize: '11.5px', color: C.ink4, marginTop: '-6px' }}>Exit arrives with order placement and will sit here, so rows never change.</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── the day line (Orders and Watchlist) ───────────────────────────────────

function DayLine({ M, issues, anyRed }) {
  const t = money(M.computed ? M.total : null, 13);
  return (
    <div data-day-line style={{ minHeight: '36px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px', background: M.over ? C.redTint : C.paper2, borderTop: '1px solid ' + C.line2, borderBottom: '1px solid ' + C.line2, fontSize: '12px', color: C.ink3 }}>
      <span style={{ fontWeight: M.computed && M.total < 0 ? 700 : 600, color: t.unknown ? C.ink5 : C.ink, fontSize: '13px', ...NUM }}>{t.text}</span>
      <span>today</span>
      <span style={{ color: C.line4 }}>·</span>
      <span style={{ whiteSpace: 'nowrap', ...NUM }}>{!M.computed ? 'limit unknown' : M.noLimit ? 'no limit set' : M.zone === 'OVER' ? 'over by ' + inr0(M.over_by || 0) : M.leftText}</span>
      <span style={{ flex: 1 }} />
      {issues > 0 && <span data-issue-count style={{ fontSize: '10px', letterSpacing: '0.1em', fontWeight: 700, color: anyRed ? C.red : C.amber }}>{issues} {issues === 1 ? 'ISSUE' : 'ISSUES'}</span>}
    </div>
  );
}

// ── orders ────────────────────────────────────────────────────────────────

function OrdersTab({ P, symbolOf, M, issues, anyRed }) {
  const orders = (P.orders && P.orders.items) || [];
  const isWorking = o => o.status_group === 'OPEN' || o.status_group === 'PARTIAL' || !o.status_group;
  const working = orders.filter(isWorking);
  const done = orders.filter(o => !isWorking(o));
  const rejects = done.filter(o => o.status_group === 'REJECTED').length;
  const awaiting = (P.orders && P.orders.awaiting_intent_count) || 0;
  const nameOf = o => { const s = o.trading_symbol || symbolOf(o.instrument_key); return legName({ trading_symbol: s, instrument_key: o.instrument_key }, symbolOf, false); };
  return (
    <div data-tab-body="orders">
      <DayLine M={M} issues={issues} anyRed={anyRed} />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '14px 14px 4px' }}>
        <span style={{ fontSize: '11px', letterSpacing: '0.14em', fontWeight: 600 }}>OPEN</span>
        <span style={{ fontSize: '11px', color: C.ink4 }}>{working.length}</span>
        <span style={{ flex: 1 }} />
        {awaiting > 0 && <span data-awaiting style={{ fontSize: '10.5px', color: C.amber, letterSpacing: '0.06em', fontWeight: 600 }}>{awaiting} AWAITING INTENT</span>}
      </div>
      {!working.length && <div style={{ padding: '8px 14px', fontSize: '12px', color: C.ink4 }}>No open orders.</div>}
      {working.map(o => {
        const terms = orderTerms(o), cl = closeness(o);
        const flag = o.after_limit_over ? 'AFTER LIMIT' : o.after_limit_red ? 'AFTER RED' : '';
        return (
          <div key={o.upstox_order_id} data-order={o.upstox_order_id} style={{ padding: '8px 14px 0', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div data-order-line1 style={{ display: 'flex', alignItems: 'baseline', gap: '8px', minWidth: 0 }}>
              <span style={{ fontSize: '14px', fontWeight: 600, flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-tip={noYear(o.trading_symbol || o.instrument_key)}>{nameOf(o)}</span>
              <span data-order-side style={{ fontSize: '12.5px', fontWeight: 700, letterSpacing: '0.05em', whiteSpace: 'nowrap', color: C.ink, ...NUM }}>{sideLine(o)}</span>
              <span style={{ flex: 1 }} />
              {o.intent
                ? <span data-order-intent style={{ fontSize: '10px', letterSpacing: '0.1em', color: C.ink4 }}>{String(o.intent).toUpperCase()}</span>
                : <PTip text="Declare the intent on the desktop" data-tag-inert style={{ fontSize: '9.5px', letterSpacing: '0.1em', fontWeight: 700, color: C.amber, border: '1px solid ' + C.amber, padding: '0 5px' }}>TAG</PTip>}
            </div>
            <div data-order-line2 style={{ display: 'flex', gap: '6px', fontSize: '11.5px', color: C.ink4, flexWrap: 'wrap', ...NUM }}>
              <span>{terms.type}</span><span>·</span><span>trig {terms.trigger}</span><span>·</span>
              <span>LTP <span style={{ color: cl.amber ? C.amber : C.ink, fontWeight: cl.amber ? 700 : 400 }}>{terms.ltp}</span></span>
              <span style={{ flex: 1 }} />
              {cl.triggered && <PTip text={'Triggered — by ' + cl.by} data-triggered style={{ color: C.amber, fontWeight: 700 }}>triggered, not filled</PTip>}
              {flag && <span style={{ color: C.red, fontWeight: 700, fontSize: '10px', letterSpacing: '0.06em' }}>{flag}</span>}
              <span>{String(o.ts || '').slice(11, 16)}</span>
            </div>
            <span data-closeness={cl.fill.toFixed(2)} data-closeness-amber={cl.amber ? 'true' : 'false'} aria-hidden="true" style={{ height: '3px', marginTop: '7px', background: C.paper3, position: 'relative', display: 'block' }}>
              <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: (cl.fill * 100).toFixed(1) + '%', background: cl.amber || cl.triggered ? C.amber : C.ink5 }} />
            </span>
          </div>
        );
      })}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '18px 14px 6px' }}>
        <span style={{ fontSize: '11px', letterSpacing: '0.14em', fontWeight: 600 }}>COMPLETED</span>
        <span style={{ fontSize: '11px', color: C.ink4 }}>{done.length}</span>
        {rejects > 0 && <span data-rejected-count style={{ fontSize: '9.5px', letterSpacing: '0.08em', fontWeight: 700, padding: '1px 5px', color: C.red, border: '1px solid ' + C.red }}>{rejects} REJECTED</span>}
      </div>
      {!done.length && <div style={{ padding: '4px 14px', fontSize: '12px', color: C.ink4 }}>Nothing completed yet.</div>}
      {done.map(o => {
        const st = doneStatus(o), edge = edgeOf(o, '');
        const ex = exceptionOf(o);
        const fl = fillLine(o);                       // upstox-083: the average fill price, the limit in small grey
        return (
          <div key={o.upstox_order_id} data-done={o.upstox_order_id} data-rejected={st.red ? 'true' : undefined}
            style={{ minHeight: '44px', display: 'grid', gridTemplateColumns: '38px minmax(0,auto) minmax(0,1fr) auto', gap: '8px', alignItems: 'center', padding: '0 14px', borderTop: '1px solid ' + C.paper3, boxShadow: st.red ? 'inset 3px 0 0 ' + C.red : 'none', fontSize: '12.5px' }}>
            <span style={{ fontSize: '11.5px', color: C.ink4, ...NUM }}>{String(o.ts || '').slice(11, 16)}</span>
            <span style={{ fontWeight: 600, maxWidth: '96px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(o)}</span>
            <span data-done-side style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25 }}>
              <PTip text={fl.reason || ''} dotted={!!fl.reason} style={{ fontWeight: 700, letterSpacing: '0.04em', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...NUM }}>{fl.text}</PTip>
              {fl.limit && <span data-order-limit style={{ fontSize: '10px', color: C.ink5 }}>{fl.limit}</span>}
            </span>
            <PTip text={(edge && edge.message) || (ex && ex.title) || ''} style={{ fontSize: '11.5px', color: st.red ? C.red : C.ink4, textAlign: 'right' }}>{st.text}</PTip>
          </div>
        );
      })}
    </div>
  );
}

// ── watchlist ─────────────────────────────────────────────────────────────

function readTab() { try { return window.localStorage.getItem(WATCH_PREF) || null; } catch (e) { return null; } }
function writeTab(v) { try { window.localStorage.setItem(WATCH_PREF, v); } catch (e) { /* view preference only */ } }

const fmt2 = v => (v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function Px({ p }) {
  if (!p) return <span data-tip="not listed for this strike" style={{ color: C.ink5, cursor: 'pointer' }} onClick={e => e.stopPropagation()}>—</span>;
  const live = p.state === 'LIVE', closed = p.state === 'CLOSED';
  return <span data-tip={p.reason || (closed ? 'last price · market closed' : live ? 'live · Kite' : '')} onClick={e => e.stopPropagation()} style={{ color: live ? C.ink : closed ? C.ink3 : C.ink5, cursor: 'pointer' }}>{p.price == null ? '—' : fmt2(p.price)}</span>;
}

function WatchTab({ P, watch, watchError, M, issues, anyRed, initialTab }) {
  const lists = (watch && watch.lists) || [];
  const [tab, setTab] = useState(() => initialTab || readTab());
  const active = lists.find(l => l.name === tab) || lists[0] || null;
  useEffect(() => { if (active) writeTab(active.name); }, [active && active.name]); // eslint-disable-line react-hooks/exhaustive-deps
  const held = heldIndex(P);
  const h = headerLine(active), st = statusLine(watch, active);
  const sides = active && active.kind === 'RULE' ? ((active.rule && active.rule.sides) || 'both') : 'both';
  const showCE = sides !== 'PE', showPE = sides !== 'CE';
  const atm = active && active.kind === 'RULE' ? (active.rows.find(r => r.atm) || {}).strike : null;
  const spot = active && active.kind === 'RULE' && active.spot != null ? Number(active.spot).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : null;
  const exp = active && active.kind === 'RULE' && active.expiry ? dayMon(active.expiry) : null;
  return (
    <div data-tab-body="watch">
      <DayLine M={M} issues={issues} anyRed={anyRed} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px 0', flexWrap: 'wrap' }}>
        {lists.map(l => {
          const on = active && active.id === l.id;
          return <button key={l.id} type="button" data-list={l.name} aria-pressed={on} onClick={() => setTab(l.name)}
            style={{ ...RESET, width: 'auto', height: '34px', display: 'flex', alignItems: 'center', padding: '0 11px', fontSize: '11px', letterSpacing: '0.06em', fontWeight: on ? 700 : 400, background: on ? C.ink : '#fff', color: on ? '#fff' : C.ink, border: '1px solid ' + (on ? C.ink : C.line), whiteSpace: 'nowrap' }}>{l.name}</button>;
        })}
      </div>
      {st && <div data-watch-status style={{ margin: '8px 14px 0', fontSize: '10px', letterSpacing: '0.1em', fontWeight: 700, color: C.red }}>{st.text}</div>}
      {!st && h && (
        <div data-watch-header style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '8px 14px', fontSize: '11.5px', color: C.ink3 }}>
          {active.kind === 'RULE'
            ? <><span style={{ fontWeight: 600, color: C.ink }}>{exp || 'no expiry'}</span>{spot && <span>· spot {spot}</span>}</>
            : <span>{h.left}</span>}
          <span style={{ flex: 1 }} />{h.last && <span style={{ color: C.ink4, ...NUM }}>{h.last}</span>}
        </div>
      )}
      {watchError && !watch && <Refusal text={'Watchlist unavailable · ' + watchError} style={{ margin: '8px 14px' }} />}
      {!watch && !watchError && <div style={{ padding: '12px 14px', fontSize: '12px', color: C.ink4 }}>Loading the watchlist…</div>}
      {active && active.kind === 'RULE' && (
        <div data-chain style={{ marginTop: st ? '8px' : 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: (showCE ? '1fr ' : '') + '96px' + (showPE ? ' 1fr' : ''), fontSize: '9.5px', letterSpacing: '0.14em', color: C.ink4, background: C.paper2, borderTop: '1px solid ' + C.line, borderBottom: '1px solid ' + C.line }}>
            {showCE && <span style={{ padding: '7px 18px', textAlign: 'right' }}>CE</span>}
            <span style={{ padding: '7px 0', textAlign: 'center', background: C.paper }}>STRIKE</span>
            {showPE && <span style={{ padding: '7px 18px' }}>PE</span>}
          </div>
          {active.rows.map(r => {
            const isAtm = !!r.atm;
            const hc = showCE ? heldChip(held, active, r.strike, 'CE') : null, hp = showPE ? heldChip(held, active, r.strike, 'PE') : null;
            const chip = t => <span data-held style={{ fontSize: '9px', letterSpacing: '0.08em', fontWeight: 700, color: C.ink3, border: '1px solid ' + C.line4, padding: '0 3px' }}>{t}</span>;
            return (
              <div key={r.strike} data-strike={r.strike} style={{ display: 'grid', gridTemplateColumns: (showCE ? '1fr ' : '') + '96px' + (showPE ? ' 1fr' : ''), fontSize: '15px', borderBottom: '1px solid ' + C.paper3, ...NUM }}>
                {showCE && <span style={{ height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', padding: '0 18px', background: atm != null && r.strike < atm ? C.paper2 : '#fff', minWidth: 0 }}>{hc && chip(hc)}<Px p={r.ce} /></span>}
                <span style={{ height: '44px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.paper, fontSize: '14px', fontWeight: isAtm ? 700 : 500, lineHeight: 1.05, boxShadow: isAtm ? 'inset 0 2px 0 ' + C.ink + ', inset 0 -2px 0 ' + C.ink : 'none' }}>
                  {Number(r.strike).toLocaleString('en-IN')}
                  {isAtm && <span style={{ fontSize: '8.5px', letterSpacing: '0.12em', color: C.ink3, fontWeight: 700 }}>ATM</span>}
                </span>
                {showPE && <span style={{ height: '44px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 18px', background: atm != null && r.strike > atm ? C.paper2 : '#fff', minWidth: 0 }}><Px p={r.pe} />{hp && chip(hp)}</span>}
              </div>
            );
          })}
          {!active.rows.length && <div style={{ padding: '12px 14px', fontSize: '12px', color: C.ink4 }}>{active.reason || 'Waiting for the first price.'}</div>}
        </div>
      )}
      {active && active.kind === 'FIXED' && (
        <div data-fixed>
          {!active.items.length && <div style={{ padding: '8px 14px', fontSize: '12px', color: C.ink4 }}>Empty — add instruments on the desktop.</div>}
          {active.items.map(it => (
            <div key={it.id} style={{ minHeight: '44px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 14px', borderTop: '1px solid ' + C.paper3, fontSize: '14px' }}>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-tip={it.instrument_key}>{it.name}</span>
              <span style={{ fontSize: '15px', ...NUM }}><Px p={it} /></span>
            </div>
          ))}
          <div style={{ padding: '10px 14px', fontSize: '11px', color: C.ink4 }}>Lists are edited on the desktop.</div>
        </div>
      )}
    </div>
  );
}

// ── the bottom bar ────────────────────────────────────────────────────────

function TabBar({ tab, onTab, issues, anyRed, nOrders }) {
  const items = [['positions', 'POSITIONS', <IconPositions key="i" />], ['orders', 'ORDERS · ' + nOrders, <IconOrders key="i" />], ['watch', 'WATCHLIST', <IconList key="i" />]];
  return (
    <nav data-tab-bar role="tablist" aria-label="Positions sections" style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 30, height: 'calc(' + TAB_H + 'px + env(safe-area-inset-bottom, 0px))', paddingBottom: 'env(safe-area-inset-bottom, 0px)', background: '#fff', borderTop: '1px solid ' + C.line, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
      {items.map(([k, label, icon]) => {
        const on = tab === k;
        return (
          <button key={k} type="button" role="tab" aria-selected={on} data-tab={k} onClick={() => onTab(k)}
            style={{ ...RESET, position: 'relative', minHeight: '44px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', color: on ? C.ink : C.ink4, boxShadow: on ? 'inset 0 2px 0 ' + C.ink : 'none', textAlign: 'center' }}>
            {icon}
            <span style={{ fontSize: '10px', letterSpacing: '0.08em', fontWeight: on ? 700 : 400, whiteSpace: 'nowrap' }}>{label}</span>
            {k === 'positions' && issues > 0 && (
              <span data-badge={anyRed ? 'red' : 'grey'} style={{ position: 'absolute', top: '4px', left: 'calc(50% + 8px)', minWidth: '14px', height: '14px', borderRadius: '7px', padding: '0 3px', background: anyRed ? C.red : C.ink4, color: '#fff', fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{issues}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ── the screen ────────────────────────────────────────────────────────────

export default function PhoneTerminal({ panel, fetchedAt, now, error, api = realApi, refetch = () => {}, stream = null, watch = null, watchError = null,
  accounts = null, account = null, onSwitchAccount = null, seed }) {
  useEffect(() => { loadIcons(); }, []);
  const [tab, setTab] = useState((seed && seed.tab) || 'positions');
  const [tapped, setTapped] = useState((seed && seed.open) || {});                       // the trader's own opens and closes; nothing else moves a group
  const [issuesOpen, setIssuesOpen] = useState(!!(seed && seed.issuesOpen));
  const [closedOpen, setClosedOpen] = useState(!!(seed && seed.closedOpen));
  const [sheet, setSheet] = useState((seed && seed.sheet) || null);   // { gid, leg }
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const rootRef = useRef(null);
  const initialOpen = useRef(null);                                // at load: groups with a problem open, the rest closed — fixed once

  const dataAgeMs = fetchedAt ? Math.max(0, now - fetchedAt) : null;
  const closeSheet = useCallback(() => setSheet(null), []);
  useSheetBack(!!sheet, closeSheet);

  const frame = { position: 'relative', width: '100%', maxWidth: '100%', overflowX: 'hidden', background: '#fff', fontFamily: FONT, color: C.ink, paddingBottom: 'calc(' + (TAB_H + 16) + 'px + env(safe-area-inset-bottom, 0px))', minHeight: 'calc(100vh - 100px)' };

  if (!panel) {
    return (
      <div ref={rootRef} className="rt-root rt-phone" data-phone style={frame}>
        <style>{SCOPED_CSS}</style>
        <AppBar accounts={accounts} account={account} status={null} onSwitch={onSwitchAccount} fresh={{ items: [], grey: '' }} dataAgeMs={dataAgeMs} tone="grey" />
        <div style={{ margin: '14px', padding: '8px 10px', fontSize: '11px', letterSpacing: '0.06em', color: C.ink2, border: '1px dashed ' + C.ink5, backgroundImage: HATCH, lineHeight: 1.4 }}>
          {error ? 'RISK TERMINAL UNAVAILABLE · ' + String(error).toUpperCase() : 'LOADING THE RISK TERMINAL…'}
        </div>
      </div>
    );
  }

  const P = panel;
  const status = P.status || {};
  const sinceFetch = s => (s || 0) + (dataAgeMs || 0) / 1000;
  const elapsed = off => sinceFetch(off.elapsed_seconds);
  const todayItems = (P.structures && P.structures.items) || [];
  const unclassified = (P.unclassified && P.unclassified.items) || [];
  const holdingItems = (P.holdings && P.holdings.items) || [];
  const symbols = {};
  [...todayItems, ...holdingItems].forEach(s => (s.legs || []).forEach(l => { symbols[l.instrument_key] = l.trading_symbol; }));
  unclassified.forEach(l => { symbols[l.instrument_key] = l.trading_symbol; });
  ((P.closed_today && P.closed_today.items) || []).forEach(c => { if (c.trading_symbol) symbols[c.instrument_key] = c.trading_symbol; });
  ((P.orders && P.orders.items) || []).forEach(o => { if (o.trading_symbol) symbols[o.instrument_key] = o.trading_symbol; });
  Object.assign(symbols, P.symbols || {});
  const symbolOf = k => symbols[k] || k || '—';
  const positionsReason = P.structures && !P.structures.positions_available ? P.structures.positions_reason : null;
  if (!initialOpen.current) initialOpen.current = Object.fromEntries(todayItems.map(s => ['t' + s.id, !!s.has_problem]));
  const isOpen = key => (key in tapped ? tapped[key] : !!initialOpen.current[key]);
  const toggle = key => setTapped(t => ({ ...t, [key]: !isOpen(key) }));

  // freshness / feeds → the status dot
  const faults = feedFaults(status, dataAgeMs, error, stream);
  const fresh = freshnessOf(status, faults);
  const tone = fresh.items.some(i => i.tone === 'red') ? 'red' : fresh.items.length ? 'amber' : status.session_state === 'closed' ? 'grey' : 'green';

  // money
  const M = moneyOf(P.money, P.settings);
  M.over_by = P.money && P.money.over_by;
  M.leftText = M.computed && !M.noLimit && P.money && P.money.left_amount != null ? inr0(P.money.left_amount) + ' left' : (M.noLimit ? 'no limit set' : '—');
  const CT = chargesToday(P.charges);

  // conditions (the server's ranking, kept)
  const inForce = (P.settings && P.settings.in_force) || null;
  const structuresById = Object.fromEntries(todayItems.map(s => [s.id, s]));
  const conds = (P.conditions && P.conditions.active) || [];
  const cviews = conds.map(k => conditionView(k, { structuresById, elapsed, symbolOf, eodTime: inForce && inForce.end_of_day_check_time }));
  const anyRed = cviews.some(c => c.red);

  // trend
  const trend = trendOf(status);
  const strip = trendStrip(status);
  const AGAINST = ['AGAINST_FADING', 'AGAINST_CONFIRMED'];
  const doSwitch = async (to, ok) => {
    if (busy) return;
    setBusy(true); setErr(null);
    const res = await api.switchReading(to, !!ok);
    setBusy(false);
    if (res.ok) { setConfirm(null); refetch(); return; }
    if (res.detail && res.detail.error === 'confirm_required') { setConfirm({ to, groups: res.detail.groups || [], message: res.detail.message }); return; }
    setErr(res.message);
  };
  const askSwitch = to => {
    const groups = todayItems.filter(x => x.grade && AGAINST.includes(x.grade.value)).map(x => ({ id: x.id, name: x.name, grade: x.grade.value }));
    if (!groups.length) { doSwitch(to, false); return; }
    const n = groups.length;
    setConfirm({ to, groups, message: n + (n === 1 ? ' group is' : ' groups are') + ' against the trend. Switching is recorded for review.' });
  };

  // groups
  const groupView = (s, isHolding) => {
    const kind = isHolding ? 'holding' : gradeKind(s.grade);
    const legs = s.legs || [];
    const ng = !isHolding && kind === 'ungraded' ? notGradedTag(s.grade, status, now) : null;
    const off = s.offside;
    const el = off ? elapsed(off) : 0;
    const redS = off && off.thresholds ? off.thresholds.red_seconds : null;
    const due = !!off && redS != null && el >= redS;
    const tags = [];
    if (kind === 'confirmed') tags.push({ kind: 'against', red: true, timer: off ? mmss(el) : '', due, tip: gradeTitle(s.grade) });
    else if (kind === 'fading') tags.push({ kind: 'against', red: false, timer: off ? mmss(el) : '', due, tip: gradeTitle(s.grade) });
    const failed = legs.find(l => l.stop_failed);
    if (failed) tags.push({ kind: 'nostop', leg: legName(failed, symbolOf, false) });
    if (ng) tags.push({ kind: 'ng', label: ng.label, tip: ng.tip || gradeTitle(s.grade), style: ng.plain ? plainNotGraded : badge('ungraded', ng.label).style });
    const pnl = legs.length ? sumKnown(legs.map(l => l.pnl)) : null;
    const m = money(pnl, 16);
    return {
      id: (isHolding ? 'h' : 't') + s.id, name: s.name, kind, legs, tags,
      tint: kind === 'confirmed' ? C.redTint : kind === 'fading' ? C.fadingTint : '#fff',
      pnl, plText: m.text, plTip: dashReason(legs, positionsReason) || '',
      nLegs: legs.length + (legs.length === 1 ? ' leg' : ' legs'),
      expiry: isHolding ? expiryText(legs) : sharedExpiry(legs),
      ch: rowCharges(s.charges, legs.map(l => ({ sym: l.trading_symbol || symbolOf(l.instrument_key), c: l.charges }))),
    };
  };
  const groups = todayItems.map(s => groupView(s, false));
  const holdings = holdingItems.map(s => groupView(s, true));
  const openLeg = (g, l) => setSheet({ gid: g.id, leg: l.leg_id ?? l.instrument_key });
  const sheetFor = () => {
    if (!sheet) return null;
    const all = [...groups, ...holdings];
    const g = all.find(x => x.id === sheet.gid);
    if (g) { const i = g.legs.findIndex(l => (l.leg_id ?? l.instrument_key) === sheet.leg); if (i >= 0) return { g, l: g.legs[i], index: { n: i + 1, of: g.legs.length } }; }
    if (sheet.gid === 'u') { const i = unclassified.findIndex(l => (l.leg_id ?? l.instrument_key) === sheet.leg); if (i >= 0) return { g: { name: 'UNCLASSIFIED' }, l: unclassified[i], index: { n: i + 1, of: unclassified.length } }; }
    return null;
  };
  const sh = sheetFor();

  // closed today
  const closedItems = (P.closed_today && P.closed_today.items) || [];
  const ct = (P.closed_today && P.closed_today.totals) || null;
  const closedPl = ct ? ct.pnl : sumKnown(closedItems.map(c => (c.pnl !== undefined ? c.pnl : c.realised)));
  const closedM = money(closedPl, 16);
  const closedCh = rowCharges(ct ? ct.charges : null, closedItems.map(c => ({ sym: c.trading_symbol || symbolOf(c.instrument_key), c: c.charges })));

  const nWorking = ((P.orders && P.orders.items) || []).filter(o => o.status_group === 'OPEN' || o.status_group === 'PARTIAL' || !o.status_group).length;

  return (
    <div ref={rootRef} className="rt-root rt-phone" data-phone data-tab-now={tab} data-account={account ? accountName(account) : undefined} style={frame}>
      <style>{SCOPED_CSS}</style>
      <TapCard rootRef={rootRef} />
      <AppBar accounts={accounts} account={account} status={status} onSwitch={onSwitchAccount} fresh={fresh} dataAgeMs={dataAgeMs} tone={tone} />

      {tab === 'positions' && (
        <div data-tab-body="positions">
          <TrendCells strip={strip} trend={trend} busy={busy} onPick={askSwitch} />
          {status.trend_note && <div style={{ margin: '4px 14px 0', fontSize: '10px', color: C.amber, fontWeight: 600, letterSpacing: '0.04em' }}>{status.trend_note}</div>}
          {err && <Refusal text={err} style={{ margin: '6px 14px 0' }} />}
          <Issues views={cviews} open={issuesOpen} onToggle={() => setIssuesOpen(o => !o)} />
          <DayBand M={M} P={P} CT={CT} />
          {positionsReason && (
            <div data-positions-reason style={{ margin: '8px 14px', padding: '8px 10px', fontSize: '11px', letterSpacing: '0.06em', color: C.ink2, border: '1px dashed ' + C.ink5, backgroundImage: HATCH, lineHeight: 1.4 }}>
              {status.feed && status.feed.upstox_signin_needed
                ? <>{'POSITION VALUES UNKNOWN · UPSTOX NOT SIGNED IN · '}<a href={UPSTOX_SIGNIN_URL} target="_blank" rel="noopener noreferrer" onClick={openUpstoxSignin} style={{ color: C.red, fontWeight: 700, letterSpacing: '0.08em' }}>SIGN IN</a></>
                : 'POSITION VALUES UNKNOWN · ' + String(positionsReason).toUpperCase()}
            </div>
          )}
          <ColumnHeader />
          {!groups.length && <div data-empty style={{ padding: '8px 14px 4px', fontSize: '12px', color: C.ink4 }}>No positions today.</div>}
          {groups.map(g => <GroupBlock key={g.id} g={g} open={isOpen(g.id)} onToggle={() => toggle(g.id)} onLeg={openLeg} symbolOf={symbolOf} />)}

          {unclassified.length > 0 && (
            <>
              <div style={{ padding: '10px 14px 2px', borderTop: '1px solid ' + C.line2, ...CAP }}>UNCLASSIFIED · {unclassified.length} · group them on the desktop</div>
              <div data-unclassified style={{ padding: '0 14px 6px', display: 'flex', flexDirection: 'column' }}>
                {unclassified.map(l => <LegRow key={l.leg_id ?? l.instrument_key} l={l} symbolOf={symbolOf} withExpiry onOpen={() => setSheet({ gid: 'u', leg: l.leg_id ?? l.instrument_key })} />)}
              </div>
            </>
          )}

          {holdings.length > 0 && (
            <>
              <div style={{ padding: '10px 14px 0', borderTop: '1px solid ' + C.line2, ...CAP }}>HOLDINGS · OUTSIDE LIMIT</div>
              {holdings.map(g => <GroupBlock key={g.id} g={g} open={isOpen(g.id)} onToggle={() => toggle(g.id)} onLeg={openLeg} symbolOf={symbolOf} />)}
            </>
          )}

          <button type="button" data-closed-today aria-expanded={closedOpen} onClick={() => setClosedOpen(o => !o)}
            style={{ ...RESET, background: C.closedTint, minHeight: '44px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '6px', alignItems: 'center', padding: '0 14px', marginTop: '6px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '10.5px', letterSpacing: '0.12em', fontWeight: 600, color: C.ink3 }}>CLOSED TODAY</span>
              <span style={{ fontSize: '11.5px', color: C.closedInk }}>{closedItems.length ? closedItems.length + ' · chg ' : 'none'}{closedItems.length > 0 && <PTip dotted text={closedCh.title} head="CHARGES">{closedCh.text}</PTip>}</span>
              <span aria-hidden="true" style={{ fontSize: '12px', color: C.ink5 }}>{closedOpen ? '▴' : '▾'}</span>
            </span>
            <span style={{ textAlign: 'right', fontSize: '16px', fontWeight: closedPl != null && closedPl < 0 ? 600 : 400, color: closedM.unknown ? C.ink5 : C.ink, ...NUM }}>{closedM.text}</span>
          </button>
          {closedOpen && (
            <div data-closed-list style={{ background: C.closedTint }}>
              {closedItems.map(c => {
                const v = c.pnl !== undefined ? c.pnl : c.realised;
                const cm = money(v, 13);
                return (
                  <div key={c.instrument_key} style={{ minHeight: '36px', display: 'grid', gridTemplateColumns: '38px minmax(0,1fr) auto', gap: '8px', alignItems: 'center', padding: '0 14px', fontSize: '12.5px', borderTop: '1px solid ' + C.line2 }}>
                    <span style={{ fontSize: '11.5px', color: C.closedInk, ...NUM }}>{hhmm(c.closed_at) || '—'}</span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{legShortOf(c.trading_symbol || symbolOf(c.instrument_key))}{c.broke_rule && <span style={{ marginLeft: '6px', fontSize: '9px', letterSpacing: '0.08em', fontWeight: 700, color: C.red }}>BROKE A RULE</span>}</span>
                    <span style={{ fontWeight: v != null && v < 0 ? 600 : 400, color: cm.unknown ? C.ink5 : C.ink, ...NUM }}>{cm.text}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'orders' && <OrdersTab P={P} symbolOf={symbolOf} M={M} issues={cviews.length} anyRed={anyRed} />}
      {tab === 'watch' && <WatchTab P={P} watch={watch} watchError={watchError} M={M} issues={cviews.length} anyRed={anyRed} initialTab={seed && seed.watchTab} />}

      <TabBar tab={tab} onTab={t => { setTab(t); }} issues={cviews.length} anyRed={anyRed} nOrders={nWorking} />

      {sh && <Sheet g={sh.g} l={sh.l} index={sh.index} symbolOf={symbolOf} positionsReason={positionsReason} account={account} onClose={closeSheet} />}

      {confirm && (
        <div data-confirm-root style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={() => setConfirm(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(26,25,23,.38)' }} />
          <div data-confirm role="dialog" aria-label="Switch the trend reading" style={{ position: 'relative', width: '100%', background: '#fff', borderTop: '1px solid #cfc9bd', padding: '16px 18px calc(18px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={CAP}>{'SWITCH TO THE ' + confirm.to + ' READING'}</div>
            <div style={{ fontSize: '13px', color: C.amber, fontWeight: 500, lineHeight: 1.45 }}>{confirm.message}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: C.line2, border: '1px solid ' + C.line2 }}>
              {confirm.groups.map(g => (
                <div key={g.id} style={{ display: 'flex', padding: '8px 10px', background: '#fff', fontSize: '12.5px', gap: '8px' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{g.name}</span>
                  <span style={{ fontSize: '9px', letterSpacing: '0.08em', color: g.grade === 'AGAINST_CONFIRMED' ? C.red : C.amber, fontWeight: 600 }}>{String(g.grade || '').replace('_', ' ')}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: C.ink3, lineHeight: 1.45 }}>A group still against under the new reading keeps its episode and timers. One no longer against ends its episode "reading switched" and is marked for the rest of today.</div>
            <Refusal text={err} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <button type="button" data-confirm-switch disabled={busy} onClick={() => doSwitch(confirm.to, true)} style={{ height: '46px', background: C.ink, color: '#fff', border: '1px solid ' + C.ink, fontSize: '11px', letterSpacing: '0.1em', fontWeight: 600 }}>SWITCH</button>
              <button type="button" data-confirm-cancel onClick={() => setConfirm(null)} style={{ height: '46px', background: '#fff', color: C.ink3, border: '1px solid ' + C.line3, fontSize: '11px', letterSpacing: '0.1em' }}>CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function legShortOf(sym) {
  return noYear(sym);
}
